import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { getWalletConnectProjectId } from "../lib/runtimeConfig";
import {
  discoverProviders,
  getSelectedRdns,
  listProviders,
  onProvidersChanged,
  onSelectionChanged,
  onWalletConnectChanged,
  selectedWallet,
  WALLETCONNECT_HINT,
  WALLETCONNECT_INFO,
  WALLETCONNECT_RDNS,
  walletConnectVersion,
  type Eip6963ProviderDetail,
} from "../lib/wallet";

/**
 * The wallets in this browser, as they announce themselves (EIP-6963): seeded
 * from what announced before mount, asked again on mount, and kept current
 * because a slow extension can announce late.
 */
export function useWalletProviders(): Eip6963ProviderDetail[] {
  const [providers, setProviders] = useState<Eip6963ProviderDetail[]>(() => listProviders());
  useEffect(() => {
    let alive = true;
    const off = onProvidersChanged(() => {
      if (alive) setProviders(listProviders());
    });
    void discoverProviders().then((found) => {
      if (alive) setProviders(found);
    });
    return () => {
      alive = false;
      off();
    };
  }, []);
  return providers;
}

/** One row of the list: an announced wallet, or WalletConnect. */
export interface WalletOption {
  rdns: string;
  name: string;
  /** A `data:` URI: EIP-6963 promises it, and WalletConnect's mark is inline. */
  icon: string;
  /** A caption on the right ("Scan with your phone"); "Last used" replaces it on the pick. */
  hint?: string;
}

/**
 * Everything the operator can sign in with: the announced wallets, plus
 * WalletConnect when the boot descriptor publishes a project id (as the admin
 * console). No project id means the row simply does not exist: never a button
 * that opens a QR sheet and hangs on a relay that answers 401.
 */
export function walletOptions(providers: readonly Eip6963ProviderDetail[]): WalletOption[] {
  const options: WalletOption[] = providers.map((p) => p.info);
  if (getWalletConnectProjectId() !== null) {
    options.push({ rdns: WALLETCONNECT_INFO.rdns, name: WALLETCONNECT_INFO.name, icon: WALLETCONNECT_INFO.icon, hint: WALLETCONNECT_HINT });
  }
  return options;
}

/** The list's rows for this browser, kept current like `useWalletProviders`. */
export function useWalletOptions(): WalletOption[] {
  const providers = useWalletProviders();
  return useMemo(() => walletOptions(providers), [providers]);
}

/** The rdns of the wallet the operator picked (it may not be in this browser any more). */
export function useSelectedRdns(): string | null {
  return useSyncExternalStore(onSelectionChanged, getSelectedRdns, getSelectedRdns);
}

/**
 * The picked wallet when it can be used, else null: an extension in this
 * browser, or WalletConnect with a live session (re-read whenever one is
 * paired, restored or ends).
 */
export function usePickedWallet(providers: readonly Eip6963ProviderDetail[]): Eip6963ProviderDetail | null {
  const rdns = useSelectedRdns();
  // Re-renders when a WalletConnect session is paired, restored or ends.
  useSyncExternalStore(onWalletConnectChanged, walletConnectVersion, walletConnectVersion);
  if (rdns === WALLETCONNECT_RDNS) return selectedWallet();
  return providers.find((p) => p.info.rdns === rdns) ?? null;
}

/** A wallet's own icon: only a `data:` image, so drawing it fetches nothing. */
export function WalletIcon({ icon, className }: { icon: string; className: string }) {
  if (!/^data:image\//i.test(icon)) return null;
  return <img className={className} src={icon} alt="" width={20} height={20} />;
}

/**
 * The list of the operator's wallets, each with its own name and icon, and
 * WalletConnect when offered. Every wallet is an explicit choice, a single one
 * included: nothing is picked for the operator. `onPick` must start the
 * connect synchronously: this click is the gesture an extension's popup needs.
 */
export function WalletPicker({
  options,
  selectedRdns,
  onPick,
  disabled = false,
  label,
}: {
  options: readonly WalletOption[];
  /** The remembered pick, marked "Last used". */
  selectedRdns: string | null;
  onPick: (rdns: string) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <div className="wallet-picker" role="group" aria-label={label}>
      <span className="wallet-picker-label">{label}</span>
      {options.map((option) => {
        const hint = option.rdns === selectedRdns ? "Last used" : option.hint;
        return (
          <button
            key={option.rdns}
            type="button"
            className={`wallet-picker-option${option.rdns === selectedRdns ? " wallet-picker-option-selected" : ""}`}
            aria-pressed={option.rdns === selectedRdns}
            disabled={disabled}
            onClick={() => onPick(option.rdns)}
          >
            <WalletIcon icon={option.icon} className="wallet-picker-icon" />
            <span className="wallet-picker-name">{option.name}</span>
            {hint !== undefined && <span className="wallet-picker-hint">{hint}</span>}
          </button>
        );
      })}
    </div>
  );
}
